import { useSyncExternalStore } from 'react';

// ─── Enums & Constants ────────────────────────────────────

export type AbilityStat = 'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha';

export type SkillId =
  | 'acrobatics' | 'animalHandling' | 'arcana' | 'athletics'
  | 'deception' | 'history' | 'insight' | 'intimidation'
  | 'investigation' | 'medicine' | 'nature' | 'perception'
  | 'performance' | 'persuasion' | 'religion' | 'sleightOfHand'
  | 'stealth' | 'survival';

export const SKILL_TO_STAT: Record<SkillId, AbilityStat> = {
  acrobatics: 'dex', animalHandling: 'wis', arcana: 'int', athletics: 'str',
  deception: 'cha', history: 'int', insight: 'wis', intimidation: 'cha',
  investigation: 'int', medicine: 'wis', nature: 'int', perception: 'wis',
  performance: 'cha', persuasion: 'cha', religion: 'int', sleightOfHand: 'dex',
  stealth: 'dex', survival: 'wis',
};

// ─── i18n migration scaffold (T101, retry 2026-05-31) ──
// SUPER MEGA cascade migrated `character-mode/sections/*` consumers к i18n keys
// `characterMode.stat.*` / `characterMode.skill.*`. Cascade scope MISSED ~11 other
// consumers (builder-mode, CombatTab, DockedState, useBreakdown, tests). To unbreak
// them без forcing мass-edit of legacy code paths, re-export the constants as
// RU-default backward-compat aliases. New code SHOULD use `statLabel(t, stat)` /
// `skillLabel(t, skill)` helpers below; legacy code can stay on the constants
// until subsequent i18n sweep.

import type { TranslationKeys } from '../i18n/types';

type TFn = (key: keyof TranslationKeys) => string;

/** Type-safe i18n lookup for stat labels. Wraps the dynamic-key assertion. */
export function statLabel(t: TFn, stat: AbilityStat): string {
  return t(('characterMode.stat.' + stat) as keyof TranslationKeys);
}

/** Type-safe i18n lookup for skill labels. */
export function skillLabel(t: TFn, skill: SkillId): string {
  return t(('characterMode.skill.' + skill) as keyof TranslationKeys);
}

/** Backward-compat: RU defaults for consumers not yet migrated к i18n. */
export const STAT_LABELS: Record<AbilityStat, string> = {
  str: 'СИЛ', dex: 'ЛОВ', con: 'ТЕЛ', int: 'ИНТ', wis: 'МДР', cha: 'ХАР',
};
export const SKILL_LABELS: Record<SkillId, string> = {
  acrobatics: 'Акробатика', animalHandling: 'Уход за животными', arcana: 'Магия',
  athletics: 'Атлетика', deception: 'Обман', history: 'История',
  insight: 'Проницательность', intimidation: 'Запугивание', investigation: 'Анализ',
  medicine: 'Медицина', nature: 'Природа', perception: 'Внимательность',
  performance: 'Выступление', persuasion: 'Убеждение', religion: 'Религия',
  sleightOfHand: 'Ловкость рук', stealth: 'Скрытность', survival: 'Выживание',
};

// ─── Sub-interfaces ───────────────────────────────────────

export interface HudIdentity {
  name: string;
  race: string;
  subrace?: string;
  className: string;
  subclass?: string;
  level: number;
  alignment?: string;
  background?: string;
  gender?: string;
  portrait?: string;
  /** BF-05: Physical parameters for Hero card (Phase 3 roleplay) */
  height?: string;
  weight?: string;
  age?: number;
  /** Legacy free-form physical description (Phase 3 fallback) */
  physicalDescription?: string;
  appearance?: string;
  quote?: string;
}

export interface HudCombat {
  initiative: number;
  /**
   * Hybrid base+bonus pattern (Wave 6). `initiative` field stores the
   * computed total (dex_mod + initiative_bonus) per template formula;
   * this field stores the user-editable bonus piece (feats, items).
   * Defaults to 0 for existing characters via template defaultValue.
   */
  initiativeBonus?: number;
  round: number;
  attacks: HudAttack[];
  actionEconomy: { action: boolean; bonus: boolean; reaction: boolean };
  /** Feet of movement spent this turn. Persisted to values.movement_spent;
   * the server's ActionEconomyResetSubscriber zeroes it on initiative:next. */
  movementSpent?: number;
  notes?: string;
}

export interface HudSpells {
  stat: AbilityStat;
  slots: Record<number, { max: number; current: number }>;
  known: HudSpell[];
  concentrationSpellId: string | null;
}

/** BF-02: Goal with checkbox, subtitle, priority (Phase 3 FR-009) */
export interface HudGoal {
  id: string;
  text: string;
  subtitle?: string;
  done: boolean;
  priority: 'arc' | 'session';
}

export type MomentType =
  | 'prologue' | 'minor' | 'encounter'
  | 'dilemma' | 'revelation' | 'turning' | 'finale';

export type CardSpan = 3 | 4 | 5 | 6 | 7 | 8 | 12;
export type CardStyle = 'plain' | 'parchment' | 'dark';

export type HudCardType =
  | 'hero' | 'triada' | 'manners' | 'backstory' | 'goals'
  | 'npc' | 'factions' | 'sessionLog' | 'moments';

export interface HudCardConfig {
  id: string;
  type: HudCardType;
  span: CardSpan;
  style: CardStyle;
  pinned: boolean;
  order: number;
  title?: string;
  content?: string;
}

export interface HudBoardConfig {
  cards: HudCardConfig[];
  autoLayout: boolean;
}

export type CardCategory = 'all' | 'personality' | 'connections' | 'past' | 'present' | 'future';

export const CARD_CATEGORY_MAP: Record<HudCardType, CardCategory[]> = {
  hero: ['personality'],
  triada: ['personality'],
  manners: ['personality'],
  backstory: ['past'],
  goals: ['present', 'future'],
  npc: ['connections'],
  factions: ['connections'],
  sessionLog: ['present'],
  moments: ['past'],
};

export const DEFAULT_BOARD_CONFIG: HudBoardConfig = {
  cards: [
    { id: 'card-hero', type: 'hero', span: 5, style: 'plain', pinned: false, order: 0 },
    { id: 'card-triada', type: 'triada', span: 4, style: 'plain', pinned: false, order: 1 },
    { id: 'card-manners', type: 'manners', span: 3, style: 'plain', pinned: false, order: 2 },
    { id: 'card-backstory', type: 'backstory', span: 7, style: 'parchment', pinned: false, order: 3 },
    { id: 'card-goals', type: 'goals', span: 5, style: 'dark', pinned: false, order: 4 },
    { id: 'card-npc', type: 'npc', span: 6, style: 'plain', pinned: false, order: 5 },
    { id: 'card-factions', type: 'factions', span: 6, style: 'plain', pinned: false, order: 6 },
    { id: 'card-sessionLog', type: 'sessionLog', span: 7, style: 'plain', pinned: false, order: 7 },
    { id: 'card-moments', type: 'moments', span: 5, style: 'plain', pinned: false, order: 8 },
  ],
  autoLayout: false,
};

export interface HudRoleplayData {
  ideal: string;
  bond: string;
  flaw: string;
  backstory: string;
  manners: string;
  /** BF-02: Changed from string to HudGoal[] for Phase 3 goals card */
  goals: HudGoal[];
  npcs: HudNpc[];
  factions: HudFaction[];
  sessionLog: HudSessionLogEntry[];
  moments: HudMoment[];
  boardConfig: HudBoardConfig;
}

export interface HudAttack {
  id: string;
  name: string;
  attackBonus: number;
  damage: string;
  damageType: string;
  meta?: string;
}

/** Casting time category — proto column «action» («действ.» / «бонус.» / «реак.» / «1мин» / «1час»). */
export type SpellActionType = 'action' | 'bonus' | 'reaction' | 'minute' | 'hour' | 'special';

/** Damage/save metadata for spell expanded view («Атака: Мдр СБ нет»). */
export interface HudSpellRoll {
  attack?: 'ranged' | 'melee';
  saveStat?: AbilityStat;
  saveHalfOnSuccess?: boolean;
  /** Damage dice of the spell at its base level (SRD damageDice; «1d4 + 1» → «1d4+1»). */
  damage?: string;
  damageType?: string;
  /** Healing dice, «MOD» = the spellcasting modifier (SRD healDice «1d8 + MOD»). */
  heal?: string;
}

export interface HudSpell {
  id: string;
  /** SRD slug when added from the catalog — stable identity carried through the
   * write-adapter → server spellbook → read-back, and the key for SRD enrichment. */
  spellSlug?: string;
  name: string;
  level: number;
  state: 'prepared' | 'always' | 'known';
  school?: string;
  concentration?: boolean;
  ritual?: boolean;
  castingTime?: string;
  range?: string;
  description?: string;
  /** V/S/M components — for proto «В · С · М» column. М is gold-tinted. */
  components?: { v?: boolean; s?: boolean; m?: boolean; materialDesc?: string };
  /** Action economy category for proto «действ. / бонус. / реак.» column. */
  actionType?: SpellActionType;
  /** Attack roll or save info for proto expanded meta-row. */
  roll?: HudSpellRoll;
  /** Spell scaling text «На N круге: …» for proto expanded paragraph. */
  higherLevels?: string;
  /** Area of the spell (SRD area_of_effect or set by hand): the aim icon of the battle HUD fits it from the caster's token. null — «no area» (clears it on save). */
  aoe?: import('@roll1/shared/spell-aoe').SpellAoe | null;
  /** Duration text «К · 1 час» for proto expanded meta-row. */
  duration?: string;
}

export interface HudFeature {
  id: string;
  name: string;
  source: string;
  description: string;
  uses?: { current: number; max: number; recharge: string };
}

export interface HudItem {
  id: string;
  name: string;
  quantity: number;
  weight?: number;
  equipped?: boolean;
  description?: string;
  /** T005: distinguishes weapons from armor/ammo/misc gear so CombatTab can
   *  surface only weapons in the «Оружие» row without name heuristics.
   *  Derived from {@link item_type} === 'weapon' — do not set independently. */
  weapon?: boolean;
  /** #1B: single source of truth for weapon/armor/shield/gear categorisation. */
  item_type?: 'weapon' | 'armor' | 'shield' | 'gear';
  /** #1B: SRD equipment slug when the item resolves to a catalogued item. */
  srd_slug?: string;
  /** #1B (Option B): manual base AC for armor tracked without an SRD slug. */
  base_ac?: number;
  /** #1B (Option B): armor weight class governing the DEX cap. */
  armor_category?: 'Light' | 'Medium' | 'Heavy';
  /** Explicit attunement flag (max 3). Independent of {@link equipped}:
   *  magic armor can be both worn and attuned. */
  attuned?: boolean;
}

export interface HudResource {
  id: string;
  name: string;
  current: number;
  max: number;
  recharge: 'short' | 'long' | 'dawn';
}

/** BF-03: 4-level relation scale (Phase 3 FR-010: foe/neutral/warm/ally) */
export type NpcRelation = 'foe' | 'neutral' | 'warm' | 'ally';

export interface HudNpc {
  id: string;
  name: string;
  relation: NpcRelation;
  notes?: string;
}

export interface HudFaction {
  id: string;
  name: string;
  loyalty: number;
  notes?: string;
}

export interface HudSessionLogEntry {
  id: string;
  date: string;
  summary: string;
  title?: string;
  number?: number;
}

export interface HudMoment {
  id: string;
  title: string;
  description: string;
  session?: string;
  /** BF-04: Type determines dot color on timeline */
  type?: MomentType;
}

export interface HudActiveEffect {
  id: string;
  name: string;
  source: string;
  duration?: string;
  description?: string;
  /** Server-set automatic end: 'auto' = at the start of its creature's turn, 'paused' = that end stopped (GM decides). */
  expiry?: 'auto' | 'paused';
}

export interface HudClassEntry {
  className: string;
  subclass?: string;
  level: number;
  hitDie: string;
}

// ─── Core Data Model ────────────────────────────────────

/**
 * Frontend character data model.
 *
 * STRUCTURAL CONTRACT (T002): HudCharacterData must be directly assignable
 * to `RulesCharacterData` (shared/systems/dnd5e/rules/index.ts) without
 * `as unknown as` casts. This is achieved because RulesCharacterData is
 * intentionally a permissive structural supertype:
 *   - identity fields (except `level`) are optional in Rules → HudIdentity's
 *     required `name`/`race`/`className` are assignable.
 *   - boardConfig.cards is `unknown[]` in Rules → HudCardConfig[] widens cleanly.
 *   - goals/npcs/moments/spells use string-typed union fields in Rules →
 *     narrower unions here (HudGoal.priority, HudNpc.relation, MomentType,
 *     HudSpell.state) are assignable.
 *   - extra optional fields on Hud types (subrace, height, V/S/M components,
 *     etc.) are tolerated — Rules ignores them.
 *
 * When TIGHTENING any field below (e.g. narrowing a string to a literal
 * union, or making an optional field required), verify the Rules side
 * still admits the new shape. When LOOSENING (adding optional fields,
 * widening unions), no change to Rules is required.
 */
export interface HudCharacterData {
  identity: HudIdentity;
  stats: Record<AbilityStat, number>;
  saves: Record<AbilityStat, 0 | 1>;
  skills: Record<SkillId, 0 | 1 | 2>;
  hp: { current: number; max: number; temp: number };
  ac: number;
  /** Walk speed (ft). Editable in Defenses. */
  speed: number;
  /** Climb/swim/fly speeds (ft). Optional — 0/absent = none. Editable in Defenses. */
  speedClimb?: number;
  speedSwim?: number;
  speedFly?: number;
  combat: HudCombat;
  spells: HudSpells;
  /**
   * T003: death-save tallies + canonical liveness status.
   *
   * `status` ('alive' | 'dying' | 'stable' | 'dead') is sourced from the
   * server's single source of truth `sheet.dnd5e.deathSaveState` and forwarded
   * by Dnd5eSheetAdapter.mapDeathSaves — the HUD never re-derives liveness from
   * HP. Mirrors DeathSaveStatus in shared/systems/dnd5e/types.ts. Optional so
   * legacy/persisted shapes and DEFAULT_CHARACTER (which omit it) stay
   * assignable; consumers should treat a missing status as 'alive'. Kept a
   * literal union (not an import) so HudCharacterData remains structurally
   * assignable to RulesCharacterData['deathSaves'], which tolerates the extra
   * optional property.
   */
  deathSaves: { successes: number; failures: number; status?: 'alive' | 'dying' | 'stable' | 'dead' };
  features: HudFeature[];
  inventory: HudItem[];
  currency: { cp: number; sp: number; ep: number; gp: number; pp: number };
  proficiencies: string[];
  /**
   * Parallel category list for `proficiencies` — each entry is
   * `"namecategory"` (category ∈ armor|weapons|tools|languages). Lets the
   * user's chosen category persist instead of being re-inferred by keyword.
   * Entries without a match here fall back to keyword categorization. Optional —
   * legacy sheets omit it.
   */
  proficiencyCats?: string[];
  resources: HudResource[];
  rp: HudRoleplayData;
  activeEffects: HudActiveEffect[];
  classData: HudClassEntry[];
  hitDice: { total: number; used: number; dieType: string };
  /** BF-11: D&D 5e Inspiration toggle (Phase 2 FR-009) */
  inspiration: boolean;
  /** BF-12: Current XP for progress bar (Phase 2 FR-011). Undefined = use level threshold. */
  xp?: number;
  /**
   * Wave 6 (2026-06-02). User-editable bonus to passive perception (magic
   * items, feats like Observant). Template formula `passive_perception` =
   * `10 + wis_mod + passive_perception_bonus` so existing characters with
   * no value get 0 via template defaultValue → final value unchanged.
   */
  passivePerceptionBonus?: number;
  /**
   * B7: Senses (ft). Optional — adapter only emits when at least one sense
   * is non-zero. Contract for darkvision matches shared/domain.ts:727-730
   * (TokenVision.darkvision: number, 0 = none). Scope currently darkvision-only;
   * blindsight/tremorsense/truesight reserved for future B7 follow-ups.
   */
  senses?: {
    darkvision?: number;
    blindsight?: number;
    tremorsense?: number;
    truesight?: number;
  };
  /**
   * #2 AC (2026-06-15). User-editable «прочий бонус» to AC (rings, feats,
   * cover). Folded into the stored `ac` server-side via recomputeDnd5eAc;
   * the DefensesSection stepper edits this rather than the total. Optional —
   * legacy/DEFAULT_CHARACTER omit it; treat missing as 0.
   */
  miscAc?: number;
  /**
   * #2 AC. Manual additive armor bonus (`armor_ac`) for characters who don't
   * track SRD armor as inventory. Folded into the stored `ac` server-side.
   */
  armorAc?: number;
  /** #2 AC. Manual shield toggle (`shield_on`) — +2 AC when on (OR'd with an
   * equipped inventory shield). */
  shieldOn?: boolean;
  /**
   * Damage resistances / immunities / vulnerabilities — free lists of D&D 5e
   * damage-type labels (RU strings, same convention as `conditions`). Stored on
   * the sheet as `damage_resistances` / `damage_immunities` /
   * `damage_vulnerabilities` (string[]). Editable in DefensesSection col 3;
   * persisted via the generic sheet:update path (WRITE_KEY_MAP). Optional —
   * legacy/DEFAULT sheets may omit them; treat missing as []. Не путать с
   * `conditions` (статусы), которые мостятся в activeEffects адаптером.
   */
  resistances?: string[];
  immunities?: string[];
  vulnerabilities?: string[];
  /**
   * #2 AC. Itemised AC contributions for the Defenses breakdown, emitted by
   * Dnd5eSheetAdapter only when the SRD equipment table is supplied to
   * fromSheet. Pieces sum to `ac` (`effectsBonus` absorbs active-effect /
   * legacy residual). Undefined → fall back to the simple ac−dexMod display.
   */
  acBreakdown?: {
    base: number;
    armorName: string | null;
    armorBonus: number;
    dexMod: number;
    shieldBonus: number;
    unarmoredBonus: number;
    /** Fighting Style: Defense (+1 in armor). */
    styleBonus?: number;
    miscBonus: number;
    effectsBonus: number;
    total: number;
  };
}

// ─── Deep Partial helper ──────────────────────────────────

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

// ─── Default Character ────────────────────────────────────

export const DEFAULT_CHARACTER: HudCharacterData = {
  identity: {
    name: 'Тэйрин из Долины',
    race: 'Полуэльф',
    subrace: 'лесной',
    className: 'Следопыт',
    subclass: 'Охотник',
    level: 5,
    alignment: 'CG',
    // BF-05: Physical parameters for Hero card
    height: '175 см',
    weight: '68 кг',
    age: 27,
    physicalDescription: 'Рост 175 см, 68 кг, 27 лет',
    appearance: 'Острые черты лица, зелёные глаза, каштановые волосы до плеч',
    quote: 'Лес помнит всё — и добро, и зло.',
  },
  stats: { str: 13, dex: 18, con: 14, int: 10, wis: 13, cha: 8 },
  saves: { str: 0, dex: 1, con: 0, int: 0, wis: 1, cha: 0 },
  skills: {
    acrobatics: 0, animalHandling: 0, arcana: 0, athletics: 1,
    deception: 0, history: 0, insight: 0, intimidation: 0,
    investigation: 0, medicine: 0, nature: 1, perception: 1,
    performance: 0, persuasion: 0, religion: 0, sleightOfHand: 0,
    stealth: 1, survival: 1,
  },
  hp: { current: 31, max: 44, temp: 0 },
  ac: 17,
  speed: 35,
  combat: {
    initiative: 4,
    round: 1,
    attacks: [
      { id: 'longbow', name: 'Длинный лук', attackBonus: 7,
        damage: '1к8+4', damageType: 'кол.', meta: '150/600' },
      { id: 'shortsword', name: 'Короткий меч', attackBonus: 7,
        damage: '1к6+4', damageType: 'кол.', meta: 'ближ.' },
    ],
    actionEconomy: { action: true, bonus: true, reaction: true },
  },
  spells: {
    stat: 'wis',
    slots: {
      1: { max: 4, current: 2 },
      2: { max: 3, current: 2 },
    },
    known: [
      { id: 'druidcraft', name: 'Дружба', level: 0,
        state: 'always', school: 'прорц',
        components: { v: false, s: true },
        actionType: 'action', range: 'само', castingTime: 'действие',
        concentration: true,
        description: 'Создаёт мелкие проявления природы.' },
      { id: 'firebolt', name: 'Огниво', level: 0,
        state: 'always', school: 'эвок',
        components: { v: true, s: true },
        actionType: 'action', range: '10 фт', castingTime: 'действие',
        roll: { attack: 'ranged' },
        description: 'Метаешь искру по цели.' },
      { id: 'hunters-mark', name: 'Метка охотника', level: 1,
        state: 'prepared', concentration: true, school: 'прорц',
        components: { v: true, s: true },
        actionType: 'bonus', range: '90 фт', castingTime: 'бонусное действие',
        duration: 'К · 1 час',
        description: 'Отметь существо в зоне видимости: +1к6 урона оружием при попадании, преимущество на Мдр (Внимательность) и Мдр (Выживание) при поиске.',
        higherLevels: 'На 3 круге: 8 часов. На 5 круге: 24 часа.' },
      { id: 'cure-wounds', name: 'Лечащее слово', level: 1,
        state: 'prepared', school: 'воплощ',
        components: { v: true },
        actionType: 'bonus', range: '60 фт', castingTime: 'бонусное действие',
        description: 'Цель восстанавливает 1к4 + мод. колд. характеристики хитов.' },
      { id: 'speak-with-animals', name: 'Друидическая речь', level: 1,
        state: 'prepared', ritual: true, school: 'прорц',
        components: { v: true, s: true },
        actionType: 'action', range: 'само', castingTime: 'действие',
        description: 'Способность общаться со зверями простыми словами.' },
      { id: 'detect-magic', name: 'Засечь магию', level: 1,
        state: 'known', ritual: true, concentration: true, school: 'прорц',
        components: { v: true, s: true },
        actionType: 'action', range: 'само', castingTime: 'действие',
        description: 'Чувствуешь присутствие магии в радиусе 30 фт.' },
      { id: 'spike-growth', name: 'Шипы', level: 2,
        state: 'prepared', concentration: true, school: 'прорц',
        components: { v: true, s: true, m: true, materialDesc: '7 острых шипов' },
        actionType: 'action', range: '90 фт', castingTime: 'действие',
        duration: 'К · 10 мин',
        roll: { saveStat: 'dex' },
        description: 'Земля в радиусе 20 фт превращается в труднопроходимую местность.' },
      { id: 'pass-without-trace', name: 'Бесследный путь', level: 2,
        state: 'prepared', concentration: true, school: 'прорц',
        components: { v: true, s: true, m: true, materialDesc: 'листья омелы' },
        actionType: 'action', range: 'само (30)', castingTime: 'действие',
        duration: 'К · 1 час',
        description: 'Союзники в радиусе 30 фт получают +10 к Скрытности.' },
      { id: 'silence', name: 'Молчание', level: 2,
        state: 'prepared', ritual: true, concentration: true, school: 'прорц',
        components: { v: true, s: true },
        actionType: 'action', range: '120 фт', castingTime: 'действие',
        description: 'Сфера 20 фт без звуков.' },
    ],
    concentrationSpellId: null,
  },
  deathSaves: { successes: 0, failures: 0, status: 'alive' },
  features: [
    { id: 'fighting-style', name: 'Стиль боя · Стрельба',
      source: 'Следопыт 2', description: '+2 к броскам атаки дальнобойным оружием.' },
    { id: 'favored-enemy', name: 'Любимый враг · Гоблиноиды',
      source: 'Следопыт 1', description: 'Преимущество на проверки Мудрости (Выживание) при выслеживании.' },
    { id: 'natural-explorer', name: 'Естественный исследователь · Лес',
      source: 'Следопыт 1', description: 'Бонус мастерства удваивается на проверках Инт/Мдр в избранной местности.' },
    { id: 'healing-spirit', name: 'Уход на природу',
      source: 'Следопыт 5', description: 'Восстанавливает 1к8+5 и снимает болезнь / яд / зачарование.',
      uses: { current: 2, max: 2, recharge: 'short' } },
    { id: 'extra-attack', name: 'Дополнительная атака',
      source: 'Следопыт 5', description: 'Атаковать дважды, когда совершаешь действие Атака.' },
  ],
  inventory: [
    { id: 'longbow', name: 'Длинный лук', quantity: 1, weight: 2, equipped: true, weapon: true },
    { id: 'shortsword', name: 'Короткий меч', quantity: 2, weight: 2, equipped: true, weapon: true },
    { id: 'chain-shirt', name: 'Кольчуга', quantity: 1, weight: 20, equipped: true },
    { id: 'arrows', name: 'Колчан (стрелы)', quantity: 60, weight: 0.05, equipped: true },
  ],
  currency: { cp: 5, sp: 12, ep: 0, gp: 47, pp: 0 },
  proficiencies: ['Простое оружие', 'Воинское оружие', 'Лёгкие доспехи',
    'Средние доспехи', 'Щиты', 'Травничество', 'Общий', 'Эльфийский', 'Гоблинский'],
  resources: [
    { id: 'favored-enemy-uses', name: 'Любимый враг', current: 2, max: 3, recharge: 'long' },
  ],
  rp: {
    ideal: 'Природа выше любых законов.',
    bond: 'Защищаю долину, в которой вырос.',
    flaw: 'Не доверяю горожанам.',
    backstory: 'Тэйрин вырос среди эльфов Долины Тихих Вод, обучаясь искусству следопыта у старейшины Кальмира. В пятнадцать лет он впервые столкнулся с гоблинами, разорявшими окраины леса, и с тех пор посвятил себя защите границ. Полуэльфийская кровь давала ему преимущество — острый слух эльфов и выносливость людей.',
    manners: 'Говорит тихо и размеренно, постоянно прислушивается к звукам леса. Привычка потирать тетиву лука при обдумывании. Не смотрит в глаза горожанам.',
    // BF-02: goals is now HudGoal[]
    goals: [
      { id: 'goal-1', text: 'Найти источник порчи в Серебряном лесу', subtitle: 'Слухи о болезни деревьев', done: false, priority: 'arc' },
      { id: 'goal-2', text: 'Раздобыть зачарованные стрелы у мастера Ольвена', subtitle: '', done: false, priority: 'session' },
      { id: 'goal-3', text: 'Предупредить деревню о набеге гоблинов', subtitle: 'Разведка подтвердила', done: true, priority: 'session' },
    ],
    // BF-03: 'ally', 'warm', 'neutral' are part of the 4-level scale (foe/neutral/warm/ally)
    npcs: [
      { id: 'npc-1', name: 'Элара', relation: 'ally', notes: 'Эльфийская друидка из Долины. Наставница в магии природы.' },
      { id: 'npc-2', name: 'Бэрин Молот', relation: 'warm', notes: 'Дварфийский кузнец. Торгует стрелами со скидкой.' },
      { id: 'npc-3', name: 'Магистр Торн', relation: 'neutral', notes: 'Бургомистр Приречья. Не доверяет нелюдям.' },
    ],
    factions: [
      { id: 'fac-1', name: 'Стражи Долины', loyalty: 4, notes: 'Орден следопытов, охраняющих лесные границы.' },
      { id: 'fac-2', name: 'Гильдия Приречья', loyalty: 2, notes: 'Торговая гильдия. Отношения натянутые.' },
    ],
    sessionLog: [
      { id: 'sl-1', date: '2026-04-20', summary: 'Группа прибыла в Приречье. Узнали о пропавшем караване.', title: 'Прибытие', number: 1 },
      { id: 'sl-2', date: '2026-04-27', summary: 'Выследили гоблинский лагерь. Освободили пленных торговцев.', title: 'Гоблинский лагерь', number: 2 },
    ],
    // BF-04: type → timeline dot color
    moments: [
      { id: 'mom-1', title: 'Клятва у дуба', description: 'Тэйрин поклялся Эларе защищать лес до последнего дыхания.', session: '1', type: 'revelation' },
      { id: 'mom-2', title: 'Засада на тракте', description: 'Первый бой с гоблинами-разведчиками. Убил вожака точным выстрелом.', session: '2', type: 'encounter' },
      { id: 'mom-3', title: 'Выбор: пленные или караван', description: 'Пришлось выбрать — спасать торговцев или преследовать убегающих гоблинов с картой лагеря.', session: '2', type: 'dilemma' },
    ],
    boardConfig: DEFAULT_BOARD_CONFIG,
  },
  activeEffects: [],
  classData: [
    { className: 'Следопыт', subclass: 'Охотник', level: 5, hitDie: 'd10' },
  ],
  hitDice: { total: 5, used: 0, dieType: 'd10' },
  // BF-11: Inspiration toggle
  inspiration: false,
  // BF-12: XP tracking (undefined = use level threshold)
  xp: 6500,
  // B7: Half-elf — darkvision 60 ft (PHB)
  senses: { darkvision: 60 },
  // Damage modifiers — empty by default (демо-персонаж без резистов).
  resistances: [],
  immunities: [],
  vulnerabilities: [],
};

// ─── Validation (BF-07: granular validation + partial recovery) ──

/** Required top-level keys with their expected typeof */
const REQUIRED_FIELDS: Array<[keyof HudCharacterData, string]> = [
  ['identity', 'object'], ['stats', 'object'], ['saves', 'object'],
  ['skills', 'object'], ['hp', 'object'], ['combat', 'object'],
  ['spells', 'object'], ['rp', 'object'],
];

const REQUIRED_ARRAYS: Array<keyof HudCharacterData> = [
  'features', 'inventory', 'proficiencies', 'resources', 'activeEffects', 'classData',
];

/** Minimal shape check — is this object "close enough" to HudCharacterData? */
function isValidHudCharacterData(v: unknown): v is HudCharacterData {
  if (typeof v !== 'object' || v === null) return false;
  const obj = v as Record<string, unknown>;

  // Check required object fields exist and are objects
  for (const [key, type] of REQUIRED_FIELDS) {
    if (!(key in obj) || typeof obj[key] !== type || obj[key] === null) return false;
  }

  // Check required arrays exist and are arrays
  for (const key of REQUIRED_ARRAYS) {
    if (!(key in obj) || !Array.isArray(obj[key])) return false;
  }

  // Deep checks on critical sub-fields
  const ident = obj.identity as Record<string, unknown>;
  if (typeof ident.name !== 'string') return false;

  const stats = obj.stats as Record<string, unknown>;
  if (typeof stats.str !== 'number') return false;

  const hp = obj.hp as Record<string, unknown>;
  if (typeof hp.current !== 'number' || typeof hp.max !== 'number') return false;

  return true;
}

/**
 * BF-07: Recover partial data — merge loaded data with DEFAULT to fill missing fields.
 * Returns valid HudCharacterData even if some fields are missing/corrupted.
 */
function recoverPartialData(raw: Record<string, unknown>): HudCharacterData | null {
  try {
    const base = cloneDefault();
    const baseObj = base as unknown as Record<string, unknown>;
    // Only merge top-level fields that look valid
    for (const key of Object.keys(baseObj)) {
      if (key in raw && raw[key] !== undefined && raw[key] !== null) {
        const val = raw[key];
        const defaultVal = baseObj[key];
        if (Array.isArray(defaultVal)) {
          if (Array.isArray(val)) {
            baseObj[key] = val;
          }
          // else keep default array
        } else if (typeof defaultVal === 'object' && defaultVal !== null) {
          if (typeof val === 'object' && val !== null) {
            baseObj[key] = { ...defaultVal as object, ...val as object };
          }
        } else {
          baseObj[key] = val;
        }
      }
    }
    return base;
  } catch {
    return null;
  }
}

/** BF-19: Deep clone DEFAULT_CHARACTER to prevent shared nested references */
function cloneDefault(): HudCharacterData {
  return JSON.parse(JSON.stringify(DEFAULT_CHARACTER));
}

/** Migrate rp data from old format to new format (backward compatibility BI-001) */
function migrateRoleplayData(rp: Record<string, unknown>): void {
  // Migrate goals: string → HudGoal[]
  if (typeof rp.goals === 'string') {
    const text = rp.goals as string;
    rp.goals = text
      ? [{ id: 'goal-migrated', text, done: false, priority: 'session' as const, subtitle: '' }]
      : [];
  }

  // Ensure boardConfig exists
  if (!rp.boardConfig || typeof rp.boardConfig !== 'object') {
    rp.boardConfig = { ...DEFAULT_BOARD_CONFIG, cards: DEFAULT_BOARD_CONFIG.cards.map(c => ({ ...c })) };
  }

  // Ensure moments have type
  if (Array.isArray(rp.moments)) {
    rp.moments = (rp.moments as HudMoment[]).map(m => ({
      ...m,
      type: m.type ?? 'minor',
    }));
  }
}

// ─── Store API ───────────────────────────────────────────

export interface CharacterHudStore {
  get(): HudCharacterData;
  set(data: HudCharacterData, source?: string): void;
  patch(partial: DeepPartial<HudCharacterData>): void;
  subscribe(listener: (data: HudCharacterData) => void): () => void;
  reset(): void;
}

const STORAGE_KEY = 'roll1:character-hud';
const DEBOUNCE_MS = 300;

/** Deep-merge a DeepPartial into a target (recurse into plain objects, replace
 * arrays/primitives wholesale). Shared with CharacterHudOverlay's optimistic
 * overlay so the two can't drift (was duplicated as `deepMergeOverlay`). */
export function deepMerge<T>(target: T, partial: DeepPartial<T>): T {
  const result = { ...target };
  for (const key of Object.keys(partial) as Array<keyof T>) {
    const val = partial[key as keyof typeof partial];
    if (val !== undefined) {
      if (
        typeof val === 'object' && val !== null && !Array.isArray(val) &&
        typeof result[key] === 'object' && result[key] !== null && !Array.isArray(result[key])
      ) {
        result[key] = deepMerge(result[key], val as DeepPartial<T[keyof T]>);
      } else {
        result[key] = val as T[keyof T];
      }
    }
  }
  return result;
}

function createCharacterHudStore(): CharacterHudStore {
  let cache: HudCharacterData;
  /** BF-06: Monotonic version counter for cross-tab conflict resolution */
  let version = 0;
  const listeners = new Set<(data: HudCharacterData) => void>();
  let writeTimer: ReturnType<typeof setTimeout> | null = null;

  // Load from localStorage (BF-07: partial recovery instead of total loss)
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (isValidHudCharacterData(parsed)) {
        // BI-001: migrate roleplay data shape if old (goals as string, missing boardConfig, etc.)
        if (parsed.rp && typeof parsed.rp === 'object') {
          migrateRoleplayData(parsed.rp as unknown as Record<string, unknown>);
        }
        cache = parsed;
      } else if (typeof parsed === 'object' && parsed !== null) {
        // BF-07: Attempt partial recovery
        const recovered = recoverPartialData(parsed as Record<string, unknown>);
        cache = recovered ?? cloneDefault();
      } else {
        cache = cloneDefault();
      }
    } else {
      cache = cloneDefault();
    }
  } catch {
    cache = cloneDefault();
  }

  // Load version from localStorage
  try {
    const storedVersion = localStorage.getItem(STORAGE_KEY + ':v');
    if (storedVersion) version = parseInt(storedVersion, 10) || 0;
  } catch { /* ignore */ }

  function notify() {
    for (const listener of listeners) {
      listener(cache);
    }
  }

  function persistNow() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
      localStorage.setItem(STORAGE_KEY + ':v', String(version));
    } catch {
      // localStorage might be full or disabled — silently degrade
    }
  }

  function schedulePersist() {
    if (writeTimer) clearTimeout(writeTimer);
    writeTimer = setTimeout(persistNow, DEBOUNCE_MS);
  }

  // BF-10: Flush pending writes on tab close
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (writeTimer) {
        clearTimeout(writeTimer);
        persistNow();
      }
    });
  }

  // BF-06: Cross-tab sync with version check
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key === STORAGE_KEY + ':v' && e.newValue) {
        const remoteVersion = parseInt(e.newValue, 10) || 0;
        if (remoteVersion <= version) return; // Stale update, ignore
      }
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue);
        if (isValidHudCharacterData(parsed)) {
          // Read remote version
          const remoteVersionStr = localStorage.getItem(STORAGE_KEY + ':v');
          const remoteVersion = remoteVersionStr ? parseInt(remoteVersionStr, 10) || 0 : 0;
          if (remoteVersion > version) {
            version = remoteVersion;
            cache = parsed;
            notify();
          }
        }
      } catch {
        // Ignore invalid JSON from other tabs
      }
    });
  }

  return {
    get() {
      return cache;
    },
    set(data: HudCharacterData, _source?: string) {
      cache = data;
      version++;
      notify();
      schedulePersist();
    },
    patch(partial: DeepPartial<HudCharacterData>) {
      cache = deepMerge(cache, partial);
      version++;
      notify();
      schedulePersist();
    },
    subscribe(listener: (data: HudCharacterData) => void) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    reset() {
      // BF-19: Deep clone to prevent shared nested references
      cache = cloneDefault();
      version++;
      notify();
      // Persist immediately on reset
      persistNow();
    },
  };
}

// ─── Singleton instance ──────────────────────────────────

export const characterHudStore = createCharacterHudStore();

// ─── D&D 5e Helpers ─────────────────────────────────────

export interface CharacterHudHelpers {
  /** Ability modifier: floor((score - 10) / 2) */
  mod(score: number): number;
  /** Proficiency bonus: ceil(level / 4) + 1 */
  pb(level: number): number;
  /** Passive check: 10 + modifier */
  passive(modifier: number): number;
  /** Format modifier: "+3" or "\u22122" */
  fmt(n: number): string;
  /** Computed skill modifier */
  activeSkillMod(data: HudCharacterData, skill: SkillId): number;
  /** Computed save modifier */
  activeSaveMod(data: HudCharacterData, stat: AbilityStat): number;
  /** Attack bonus (stat mod + prof bonus) */
  attackBonus(data: HudCharacterData, stat: AbilityStat): number;
  /** Spell save DC (8 + prof bonus + spellcasting stat mod) */
  spellDC(data: HudCharacterData): number;
}

export const helpers: CharacterHudHelpers = {
  mod(score: number): number {
    return Math.floor((score - 10) / 2);
  },
  pb(level: number): number {
    return Math.ceil(level / 4) + 1;
  },
  passive(modifier: number): number {
    return 10 + modifier;
  },
  fmt(n: number): string {
    return n >= 0 ? `+${n}` : `\u2212${Math.abs(n)}`;
  },
  // BF-17: slotTotal removed (was dead code with incorrect half-caster-only table)
  activeSkillMod(data: HudCharacterData, skill: SkillId): number {
    const stat = SKILL_TO_STAT[skill];
    const statMod = helpers.mod(data.stats[stat]);
    const profLevel = data.skills[skill]; // 0 = none, 1 = proficient, 2 = expertise
    const pb = helpers.pb(data.identity.level);
    return statMod + profLevel * pb;
  },
  activeSaveMod(data: HudCharacterData, stat: AbilityStat): number {
    const statMod = helpers.mod(data.stats[stat]);
    const proficient = data.saves[stat];
    const pb = helpers.pb(data.identity.level);
    return statMod + proficient * pb;
  },
  attackBonus(data: HudCharacterData, stat: AbilityStat): number {
    return helpers.mod(data.stats[stat]) + helpers.pb(data.identity.level);
  },
  spellDC(data: HudCharacterData): number {
    return 8 + helpers.pb(data.identity.level) + helpers.mod(data.stats[data.spells.stat]);
  },
};

// ─── React Hook ────────────────────────────────────────

/**
 * Options for the factory overload of useCharacterHudStore.
 * When `sheetData` is provided, the hook returns a read-only derived store
 * with the adapter-mapped data instead of the localStorage singleton.
 */
export interface UseCharacterHudStoreOpts {
  /** Pre-adapted character data (from Dnd5eSheetAdapter.fromSheet) */
  sheetData?: HudCharacterData | null;
  /** Dnd5eRules instance for rule-aware computations */
  rules?: unknown;
  /** Sheet read role (informational, passed through) */
  role?: string;
  /**
   * Called when consumers patch in adapter mode.
   * Replaces no-op store.patch — wire to CharacterHudOverlay.dispatch
   * for optimistic overlay + debounced WS dispatch.
   * Fixes BF-001, BF-005 (consumer wiring incomplete).
   */
  onPatch?: (partial: DeepPartial<HudCharacterData>) => void;
}

/** Return type — backward-compatible, with optional rules */
export interface UseCharacterHudStoreReturn {
  data: HudCharacterData;
  store: CharacterHudStore;
  helpers: CharacterHudHelpers;
  rules?: unknown;
}

export function useCharacterHudStore(opts?: UseCharacterHudStoreOpts): UseCharacterHudStoreReturn {
  // Default mode: singleton localStorage store (BI-001 exact behavior)
  const singletonData = useSyncExternalStore(
    characterHudStore.subscribe,
    characterHudStore.get,
    characterHudStore.get,
  );

  // If sheetData is provided and not null, use adapter-mode (read-only derived store)
  if (opts?.sheetData) {
    const readOnlyStore: CharacterHudStore = {
      get() { return opts.sheetData!; },
      set(_data: HudCharacterData, _source?: string) {
        console.warn('[character-hud-store] set() called on read-only adapter store — ignored');
      },
      patch(partial: DeepPartial<HudCharacterData>) {
        // BF-001 / BF-005 fix: route patches through CharacterHudOverlay.dispatch
        // for optimistic overlay + debounced WS send when onPatch is wired.
        if (opts.onPatch) {
          opts.onPatch(partial);
        } else {
          console.warn('[character-hud-store] patch() called on read-only adapter store without onPatch — ignored');
        }
      },
      subscribe(_listener: (data: HudCharacterData) => void) {
        // No-op subscriber for adapter mode — data is externally driven
        return () => {};
      },
      reset() {
        console.warn('[character-hud-store] reset() called on read-only adapter store — ignored');
      },
    };
    return {
      data: opts.sheetData,
      store: readOnlyStore,
      helpers,
      rules: opts.rules,
    };
  }

  // Default: singleton mode (BI-001)
  return { data: singletonData, store: characterHudStore, helpers, rules: opts?.rules };
}

/**
 * T302: Per-field selector hook over the singleton store.
 * Subscribes via useSyncExternalStore with a getSnapshot that reads only the
 * requested top-level field, so consumers re-render only when that slice
 * changes (referentially). Singleton-only — adapter mode is out of scope.
 */
export function useCharacterHudStoreField<K extends keyof HudCharacterData>(
  field: K,
): HudCharacterData[K] {
  const getField = (): HudCharacterData[K] => characterHudStore.get()[field];
  return useSyncExternalStore(characterHudStore.subscribe, getField, getField);
}

// NOTE: the former "Granular Roleplay inbound patch API"
// (useCharacterHudStore.getState().applyRp*) was removed when rp echoes were
// re-routed into the per-sheet charSheets store (useCharacterSheets.handleRp*,
// wired in useMultiplayerSync). The singleton variant ignored the broadcast
// sheetId and patched the localStorage demo character with ANY sheet's rp echo,
// while the adapter-mode HUD never read it — both wrong.
